<template>
  <section class="rounded-sm bg-black/30 p-3">
    <header v-if="showHeader" class="mb-3 flex items-center justify-between">
      <div class="flex items-center gap-2 cursor-pointer" @click="isExpanded = !isExpanded">
        <i :class="isExpanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
        <h3 class="text-sm font-semibold text-white/90">Anomaly details</h3>
      </div>
      <div class="flex items-center gap-2">
        <span
          class="rounded px-2 py-1 uppercase tracking-wide text-xxs"
          :class="severityBadgeClass(incident.severity)"
        >
          {{ severityLabel(incident.severity) }}
        </span>
        <button
          class="inline-flex h-7 w-7 items-center justify-center rounded transition-colors hover:bg-black/55"
          title="Close"
          @click="$emit('close')"
        >
          <i class="bi bly-close"></i>
        </button>
      </div>
    </header>

    <div v-if="isExpanded" class="space-y-3 text-sm text-white/80">
      <div class="grid grid-cols-2 gap-2">
        <div>
          <div class="text-xs uppercase text-white/55">Metric</div>
          <div class="font-medium">{{ incident.metricName || '-' }}</div>
        </div>
        <div>
          <div class="text-xs uppercase text-white/55">Resource</div>
          <div class="flex items-center gap-1 font-medium" :class="resourceMeta.colorClass">
            <span
              v-if="resourceLabel"
              class="inline-flex h-4 min-w-[16px] shrink-0 items-center justify-center rounded px-1 text-[10px] font-bold leading-none"
              :class="resourceLabel.badge"
              :title="resourceLabel.name"
            >{{ resourceLabel.letter }}</span>
            <i :class="resourceMeta.icon"></i>
            <span class="truncate">{{ incident.resourceName || '-' }}</span>
            <span class="text-[10px] uppercase opacity-80">{{ resourceMeta.label }}</span>
          </div>
          <div
            v-if="incident.resourceId"
            class="mt-0.5 text-[10px] text-white/40 break-all"
            :title="incident.resourceId"
          >
            id: {{ incident.resourceId }}
          </div>
        </div>
      </div>

      <div v-if="groupMeta.id || groupMeta.name">
        <div class="text-xs uppercase text-white/55">Signal group</div>
        <div class="flex flex-wrap items-center gap-2 font-medium" :title="groupMeta.indicates">
          <span
            v-if="groupMeta.id"
            class="rounded bg-white/10 px-1.5 py-0.5 text-xs font-bold tracking-wide text-white/85"
          >{{ groupMeta.id }}</span>
          <span>{{ groupMeta.name }}</span>
          <span v-if="groupMeta.class" class="text-[10px] uppercase tracking-wide text-white/40">{{ groupMeta.class }}</span>
        </div>
        <div v-if="groupMeta.indicates" class="mt-0.5 text-xs text-white/50">{{ groupMeta.indicates }}</div>
      </div>

      <div>
        <div class="text-xs uppercase text-white/55">Range</div>
        <div>{{ formatDate(incident.startMs) }} -> {{ formatDate(incident.endMs) }}</div>
      </div>

      <!-- Rough idea of the values seen during the anomaly vs the threshold. -->
      <div v-if="hasMetricStats">
        <div class="text-xs uppercase text-white/55">
          Values<span v-if="metricStats.unit"> ({{ metricStats.unit }})</span>
        </div>
        <div class="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-sm bg-black/40 p-2">
          <span v-if="metricStats.min != null">
            <span class="text-xs uppercase text-white/55">min</span>
            <b class="ml-1 text-white/90">{{ formatStat(metricStats.min) }}</b>
          </span>
          <span v-if="metricStats.avg != null">
            <span class="text-xs uppercase text-white/55">avg</span>
            <b class="ml-1 text-white/90">{{ formatStat(metricStats.avg) }}</b>
          </span>
          <span v-if="metricStats.max != null">
            <span class="text-xs uppercase text-white/55">max</span>
            <b class="ml-1 text-white/90">{{ formatStat(metricStats.max) }}</b>
          </span>
          <span v-if="metricStats.value != null">
            <span class="text-xs uppercase text-white/55">value</span>
            <b class="ml-1 text-white/90">{{ formatStat(metricStats.value) }}</b>
          </span>
          <span v-if="metricStats.threshold != null" class="text-amber-200">
            <span class="text-xs uppercase text-amber-200/70">threshold</span>
            <b class="ml-1">{{ formatStat(metricStats.threshold) }}</b>
          </span>
        </div>
      </div>

      <div v-if="hasAiSection">
        <div class="text-xs uppercase text-white/55">AI analysis</div>
        <div class="flex flex-col gap-2 rounded-sm bg-black/40 p-2">
          <div v-if="aiSchema.whatHappened">
            <div class="mb-0.5 text-xs font-semibold text-white/70">What happened</div>
            <div class="whitespace-pre-line">{{ aiSchema.whatHappened }}</div>
          </div>
          <div v-if="aiSchema.probableCause">
            <div class="mb-0.5 text-xs font-semibold text-white/70">Probable cause</div>
            <div class="whitespace-pre-line">{{ aiSchema.probableCause }}</div>
          </div>
          <div v-if="aiSchema.recoveryReason">
            <div class="mb-0.5 text-xs font-semibold text-white/70">Recovery reason</div>
            <div class="whitespace-pre-line">{{ aiSchema.recoveryReason }}</div>
          </div>
          <div v-if="aiSchema.model || aiSchema.generatedAt" class="flex flex-wrap gap-3 text-xs text-white/55">
            <span v-if="aiSchema.model"><b>Model:</b> {{ aiSchema.model }}</span>
            <span v-if="aiSchema.generatedAt"><b>Generated:</b> {{ formatDate(aiSchema.generatedAt) }}</span>
          </div>
        </div>
      </div>

      <div v-else class="rounded-sm bg-black/40 p-2">
        <div class="mb-2 text-xs text-white/55">No AI analysis for this anomaly yet.</div>
        <button
          class="h-7 px-3 text-xs font-bold tracking-tight rounded cursor-pointer transition-colors bg-yellow-600 text-black hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-40"
          :disabled="analyzing"
          @click="$emit('analyze')"
        >
          <i v-if="analyzing" class="el-icon-loading mr-1"></i>
          Analyze all incidents
        </button>
      </div>

      <div v-if="grafanaLinks.length">
        <div class="text-xs uppercase text-white/55">Grafana</div>
        <div class="flex flex-col gap-1 rounded-sm bg-black/40 p-2">
          <a
            v-for="(link, index) in grafanaLinks"
            :key="`${link.url}-${index}`"
            :href="link.url"
            target="_blank"
            rel="noopener noreferrer"
            class="text-xs text-sky-300 underline decoration-sky-500/60 hover:text-sky-200"
          >
            {{ link.label }}
          </a>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed } from 'vue';
import moment from 'moment';
import { resourceTypeMeta } from '../utils/eventResourceType';
import { severityLabel, severityBadgeClass } from '../utils/timelineScale';
import { signalGroupMeta } from '../utils/signalGroups';

const isAiSchema = (value) => {
  if (!value || typeof value !== 'object') return false;
  return !!(value.whatHappened || value.probableCause || value.recoveryReason || value.model || value.generatedAt);
};

const props = defineProps({
  incident: {
    type: Object,
    required: true,
  },
  event: {
    type: Object,
    default: null,
  },
  fallbackRange: {
    type: Object,
    default: () => ({ from: null, to: null }),
  },
  analyzing: {
    type: Boolean,
    default: false,
  },
  // Per-incident A/B resource marker ({ letter, badge, name }) from the parent
  // incident timeline; null when the incident has a single resource.
  resourceLabel: {
    type: Object,
    default: null,
  },
  showHeader: {
    type: Boolean,
    default: true,
  },
});

defineEmits(['close', 'analyze']);

const isExpanded = ref(true);
const baseGrafana = 'https://srlogs.elas.tech/d/a1c79dbe-ad33-4b86-b35a-abf7649e3d7b/interface-view';
// AWS-hosted Grafana workspace for the MediaConnect input/output dashboards.
// This is a different host from `baseGrafana` above and must not be derived
// from it (the previous code concatenated the srlogs path onto these links,
// producing a broken URL).
const awsGrafanaBase = 'https://g-931d259b22.grafana-workspace.eu-west-1.amazonaws.com';

const resourceMeta = computed(() => resourceTypeMeta(props.incident && props.incident.resourceType));
const groupMeta = computed(() => signalGroupMeta(
  props.incident && props.incident.groupId,
  props.incident && props.incident.groupName,
));
const parsedAiValue = computed(() => {
  const value = props.incident && props.incident.aiAnalysis;
  if (!value) return null;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return null;
  try {
    return JSON.parse(value);
  } catch (error) {
    return value;
  }
});
const aiSchema = computed(() => {
  if (isAiSchema(parsedAiValue.value)) return parsedAiValue.value;
  return {};
});
const hasAiSection = computed(() => Object.keys(aiSchema.value).length > 0);
// Best-effort min/max/avg/value/threshold for the anomaly's metric, pulled
// from the raw report item (field names vary, so we match common keys and
// look one level into the usual stat containers).
const metricStats = computed(() => {
  const raw = (props.incident && props.incident.raw) || props.incident || {};
      const containers = [
        raw,
        raw.stats, raw.stat, raw.statistics,
        raw.metric, raw.metrics, raw.metricStats,
        raw.values, raw.value, raw.summary, raw.data,
      ].filter((c) => c && typeof c === 'object');

      const toNum = (v) => {
        if (typeof v === 'number' && Number.isFinite(v)) return v;
        if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
        return null;
      };
      // Exact-ish key match to avoid false positives like "maxBitrate".
      const find = (keys) => {
        for (let i = 0; i < containers.length; i += 1) {
          const obj = containers[i];
          const objKeys = Object.keys(obj);
          for (let j = 0; j < objKeys.length; j += 1) {
            if (keys.includes(objKeys[j].toLowerCase())) {
              const num = toNum(obj[objKeys[j]]);
              if (num !== null) return num;
            }
          }
        }
        return null;
      };
      const findStr = (keys) => {
        for (let i = 0; i < containers.length; i += 1) {
          const obj = containers[i];
          const objKeys = Object.keys(obj);
          for (let j = 0; j < objKeys.length; j += 1) {
            if (keys.includes(objKeys[j].toLowerCase()) && typeof obj[objKeys[j]] === 'string') {
              return obj[objKeys[j]];
            }
          }
        }
        return '';
      };

  return {
    min: find(['min', 'minimum', 'minvalue']),
    max: find(['max', 'maximum', 'maxvalue']),
    avg: find(['avg', 'average', 'mean', 'avgvalue']),
    value: find(['value', 'current', 'observed', 'actual', 'val']),
    threshold: find(['threshold', 'thresholdvalue', 'limit', 'breachvalue']),
    unit: findStr(['unit', 'units']),
  };
});
const hasMetricStats = computed(() => {
  const s = metricStats.value;
  return [s.min, s.max, s.avg, s.value, s.threshold].some((v) => v != null);
});
const grafanaLinks = computed(() => {
      const links = [];
      const visited = new Set();
      const fallbackCandidates = {
        inputNames: new Set(),
        outputNames: new Set(),
        regions: new Set(),
      };

      const toMs = (value) => {
        if (value == null) return null;
        if (typeof value === 'number' && Number.isFinite(value)) return value;
        const parsed = Date.parse(value);
        return Number.isNaN(parsed) ? null : parsed;
      };

      const eventFrom = toMs(props.event?.lineupTime || props.event?.startTime || props.event?.serviceStartTime);
      const eventTo = toMs(props.event?.endTime || props.event?.serviceEndTime);
      const rangeFrom = eventFrom || toMs(props.fallbackRange?.from);
      const rangeTo = eventTo || toMs(props.fallbackRange?.to);

      // Stream Router (type ROUTE, input or output) anomalies live on a separate
      // Grafana (srlogs) "interface-view" dashboard keyed by the interface id and
      // host from the stream-router config. The normalizer keeps the raw report
      // item under `raw`, so the config may be at `incident.config` or
      // `incident.raw.config`.
      const sourceType = `${(props.incident && props.incident.sourceType) || ''}`.toUpperCase();
      let routeConfig = (props.incident && props.incident.config)
        || (props.incident && props.incident.raw && props.incident.raw.config)
        || null;
      // The input/output route config is persisted as a JSON string
      // (`{ interfaceId, interfaceName, streamManagerId, type:'SR' }`). When it
      // arrives unparsed, `routeConfig.interfaceId` is undefined and the whole
      // Stream Router branch was silently skipped — so parse it first.
      if (typeof routeConfig === 'string') {
        try {
          routeConfig = JSON.parse(routeConfig);
        } catch (error) {
          routeConfig = null;
        }
      }
      // Enter when it's a ROUTE source, or whenever the config itself is a stream
      // router interface (type 'SR'), in case sourceType isn't propagated.
      const isStreamRouter = sourceType === 'ROUTE'
        || (routeConfig && `${routeConfig.type || ''}`.toUpperCase() === 'SR');
      // Interface id from the config (several spellings), falling back to the
      // anomaly's resourceId.
      const interfaceId = (routeConfig
        && (routeConfig.interfaceId || routeConfig.interface || routeConfig.interfaceID))
        || (props.incident && props.incident.resourceId)
        || '';
      if (isStreamRouter && interfaceId) {
        const toIso = (ms) => (ms ? new Date(ms).toISOString() : null);
        // Use the event range (falls back to the anomaly window / filter range).
        const from = toIso(rangeFrom || props.incident.startMs);
        const to = toIso(rangeTo || props.incident.endMs);
        const params = new URLSearchParams({ orgId: '1' });
        if (from) params.set('from', from);
        if (to) params.set('to', to);
        params.set('timezone', 'browser');
        params.set('var-interface', interfaceId);
        // The config host lacks the 'uk' segment (e.g. img-sr04); the dashboard
        // expects it inserted as the second segment (img-uk-sr04).
        const withUk = (host) => {
          if (!host) return host;
          const parts = String(host).split('-');
          if (parts.length >= 2 && parts[1] !== 'uk') parts.splice(1, 0, 'uk');
          return parts.join('-');
        };
        // Host can be under a few field names; if none match, scan the config
        // for an `img-…`/`img-uk-…` stream-router hostname.
        const scanHost = (cfg) => {
          const values = Object.values(cfg || {});
          for (let i = 0; i < values.length; i += 1) {
            const v = values[i];
            if (typeof v === 'string' && /^img[-.]/i.test(v)) return v;
          }
          return null;
        };
        const rawHost = routeConfig
          ? (routeConfig.srHost || routeConfig.host || routeConfig.streamManagerHost
            || routeConfig.hostname || scanHost(routeConfig))
          : null;
        const hostWithUk = withUk(rawHost);
        if (hostWithUk) params.set('var-host', hostWithUk);
        const label = `Open Stream Router (${hostWithUk || interfaceId})`;
        return [{ label, url: `${baseGrafana}?${params.toString()}` }];
      }

      const extractRegion = (text) => {
        if (typeof text !== 'string') return null;
        const match = text.match(/\b(us|eu|ap|sa|ca|af|me)-(north|south|east|west|central|northeast|northwest|southeast|southwest)-\d\b/i);
        return match ? match[0].toLowerCase() : null;
      };

      const appendTimeRange = (url) => {
        if (!rangeFrom || !rangeTo || rangeTo <= rangeFrom) return url;
        try {
          const parsed = new URL(url);
          parsed.searchParams.set('from', String(rangeFrom));
          parsed.searchParams.set('to', String(rangeTo));
          return parsed.toString();
        } catch (error) {
          return url;
        }
      };

      const walk = (value, path = []) => {
        if (value == null) return;
        if (typeof value === 'string') {
          if (!value.includes('grafana-workspace')) return;
          if (!value.startsWith('http://') && !value.startsWith('https://')) return;
          const withRange = appendTimeRange(value);
          if (visited.has(withRange)) return;
          visited.add(withRange);
          const label = path.length ? path.join('.') : `Link ${links.length + 1}`;
          links.push({ label, url: withRange });
          return;
        }
        if (Array.isArray(value)) {
          value.forEach((entry, index) => walk(entry, [...path, String(index)]));
          return;
        }
        if (typeof value === 'object') {
          Object.keys(value).forEach((key) => {
            const v = value[key];
            const lower = key.toLowerCase();

            if (typeof v === 'string') {
              if (lower.includes('inputname') || lower === 'input' || lower === 'input_name') fallbackCandidates.inputNames.add(v);
              if (lower.includes('outputname') || lower === 'output' || lower === 'output_name') fallbackCandidates.outputNames.add(v);
              if (lower.includes('region')) {
                const reg = extractRegion(v);
                if (reg) fallbackCandidates.regions.add(reg);
              }
              const regAny = extractRegion(v);
              if (regAny) fallbackCandidates.regions.add(regAny);
            }
            walk(value[key], [...path, key]);
          });
        }
      };

      walk(props.incident);
      walk(props.event);

      if (fallbackCandidates.regions.size === 0) {
        fallbackCandidates.regions.add('eu-central-1');
      }

      const pushFallbackLink = (label, url) => {
        const finalUrl = appendTimeRange(url);
        if (visited.has(finalUrl)) return;
        visited.add(finalUrl);
        links.push({ label, url: finalUrl });
      };

      if (links.length === 0) {
        const regions = Array.from(fallbackCandidates.regions);
        const inputNames = Array.from(fallbackCandidates.inputNames);
        const outputNames = Array.from(fallbackCandidates.outputNames);
        const resourceName = props.incident && props.incident.resourceName;
        const isOutput = (props.incident && props.incident.resourceType) === 'OUTPUT';
        const isInput = (props.incident && props.incident.resourceType) === 'INPUT';
        if (resourceName && isInput) inputNames.push(resourceName);
        if (resourceName && isOutput) outputNames.push(resourceName);

        regions.forEach((region) => {
          if (inputNames.length > 0) {
            inputNames.forEach((name) => {
              const url = `${awsGrafanaBase}/d/ffgbmgfjc8dtsd/media-connect-input-monitor?orgId=1&var-InputName=${encodeURIComponent(name)}&var-AwsRegion=${encodeURIComponent(region)}`;
              pushFallbackLink(`Open Grafana Input (${name}, ${region})`, url);
            });
          }
          if (outputNames.length > 0) {
            outputNames.forEach((name) => {
              const url = `${awsGrafanaBase}/d/ffgbmgfjc8dtwrx/media-connect-output-monitor?orgId=1&refresh=5s&var-OutputName=${encodeURIComponent(name)}&var-AwsRegion=${encodeURIComponent(region)}`;
              pushFallbackLink(`Open Grafana Output (${name}, ${region})`, url);
            });
          }
        });
      }

      return links.map((link, index) => ({
        ...link,
        label: link.label || `Open Grafana ${index + 1}`,
      }));
});

function formatDate(value) {
  if (!value) return '-';
  return moment.utc(value).format('DD/MM/YYYY HH:mm:ss');
}
function formatStat(value) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '-';
  if (Number.isInteger(value)) return value.toLocaleString();
  // Trim to 3 significant-ish decimals without trailing zeros.
  return Number(value.toFixed(3)).toString();
}
</script>
